import React, { useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TranslationProvider } from './context/TranslationContext';
import { ErrorBoundary } from 'react-error-boundary';
import Header from './components/Header';
import TextTranslator from './components/TextTranslator';
import AuthPage from './components/auth/AuthPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useAuth } from './hooks/useAuth';

// Lazy load non-critical components
const DocumentTranslator = lazy(() => import('./components/DocumentTranslator'));
const WebsiteTranslator = lazy(() => import('./components/WebsiteTranslator'));
const PhrasebookPanel = lazy(() => import('./components/PhrasebookPanel'));
const HistoryPanel = lazy(() => import('./components/HistoryPanel'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => (
  <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
    <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">Something went wrong</h2>
    <p className="text-red-600 dark:text-red-300 mt-2">{error.message}</p>
    <button
      onClick={resetErrorBoundary}
      className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      Try again
    </button>
  </div>
);

// Loading Component
const LoadingFallback = () => (
  <div role="status" className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#00A86B] border-t-transparent"></div>
    <span className="sr-only">Loading...</span>
  </div>
);

function App() {
  const [activeTab, setActiveTab] = useState('text');
  const { isAuthenticated, isAdmin } = useAuth();

  return (
    <TranslationProvider>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-300">
        <Header activeTab={activeTab} setActiveTab={setActiveTab} />
        
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/auth" element={<AuthPage />} />

                {/* Protected routes requiring authentication */}
                <Route path="/" element={
                  <ProtectedRoute requiresSubscription>
                    <TextTranslator />
                  </ProtectedRoute>
                } />
                
                <Route path="/document" element={
                  <ProtectedRoute requiresSubscription>
                    <DocumentTranslator />
                  </ProtectedRoute>
                } />
                
                <Route path="/website" element={
                  <ProtectedRoute requiresSubscription>
                    <WebsiteTranslator />
                  </ProtectedRoute>
                } />
                
                <Route path="/phrasebook" element={
                  <ProtectedRoute>
                    <PhrasebookPanel />
                  </ProtectedRoute>
                } />
                
                <Route path="/history" element={
                  <ProtectedRoute>
                    <HistoryPanel />
                  </ProtectedRoute>
                } />

                {/* Admin routes */}
                <Route path="/admin" element={
                  isAuthenticated && isAdmin ? (
                    <AdminPanel />
                  ) : (
                    <Navigate to="/auth" replace />
                  )
                } />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} TranslateNow by JSB-Tech LLC • Powered by Neural Machine Translation
            </p>
          </div>
        </footer>
      </div>
    </TranslationProvider>
  );
}

export default App;